import { toast } from "react-toastify";
import SidebarField from "../../../components/Sidebar/SidebarField";
import { FormField } from "../../../components/FormField";
import Button from "../../../components/Button";
import TextInput from "../../../components/TextInput";
import { Formik, Form, Field, FieldArray, ErrorMessage } from "formik";
import { IoMdAddCircleOutline, IoMdRemoveCircleOutline } from "react-icons/io";
import * as Yup from "yup";

import { InventoryApi } from "../../../apis/inventory.api";
import { useContext, useEffect, useMemo, useState } from "react";
import { productApi } from "../../../apis/ProductListing.apis";
import LoaderContext from "../../../components/loader";
import masterApi from "../../../apis/master.api";
import OrderApi from "../../../apis/order.apis";

// Helper: convert populated object → _id
const toId = (v) => (v && typeof v === "object" ? v._id : v) || "";

const validationSchema = Yup.object({
  dateOfIssue: Yup.date().required("Date of Issue is required"),
  issuedTo: Yup.string().required("Issued To is required"),
  orderReference: Yup.string().required("Order Reference is required"),
  vendorLocation: Yup.string().required("Vendor Location is required"),
  receivedBy: Yup.string().required("Received By is required"),
  attachments: Yup.mixed().nullable(),
  itemDetails: Yup.array()
    .min(1, "At least one item is required")
    .of(
      Yup.object({
        itemCode: Yup.string().required("Item Code is required"),
        itemDescription: Yup.string().required("Item Description is required"),
        quantity: Yup.number().required("Quantity is required").positive(),
        unit: Yup.string().required("Unit is required"),
        conditionOfGoods: Yup.string().required("Condition of Goods is required"),
        unitCost: Yup.number().required("Unit Cost is required").positive(),
        totalCost: Yup.number().required("Total Cost is required").positive(),
        comments: Yup.string().nullable(),
      })
    ),
});

function AddGIV({ open, handleClose, data, getAllData }) {
  const [allProducts, setAllProducts] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [unitMasterData, setUnitMasterData] = useState([]);
  const { handleLoading } = useContext(LoaderContext);
  const [isFullWidth, setIsFullWidth] = useState(false);
console.log("data:",data)
  const initialValues = useMemo(() => {
    return {
      issueNumber: data?.issueNumber || "",
      dateOfIssue: data?.dateOfIssue
        ? String(data.dateOfIssue).slice(0, 10)
        : "",
      issuedTo: data?.issuedTo || "",
      orderReference: toId(data?.orderReference),
      vendorLocation: data?.vendorLocation || "",
      receivedBy: data?.receivedBy || "",
      attachments: data?.attachments||null,

      itemDetails:
        data?.itemDetails?.map((item) => ({
          itemCode: toId(item?.itemCode),
          itemDescription: item?.itemDescription || "",
          quantity: item?.quantity ?? "",
          unit: toId(item?.unit),
          conditionOfGoods:
            typeof item?.conditionOfGoods === "object"
              ? item?.conditionOfGoods?.name || ""
              : item?.conditionOfGoods || "",
          unitCost: item?.unitCost ?? "",
          totalCost: item?.totalCost ?? "",
          comments: item?.comments || "",
        })) || [
          {
            itemCode: "",
            itemDescription: "",
            quantity: "",
            unit: "",
            conditionOfGoods: "",
            unitCost: "",
            totalCost: "",
            comments: "",
          },
        ],
    };
  }, [data]);

  const getAllProducts = async () => {
    handleLoading(true);
    try {
      const res = await productApi.getAllApprovedProduct();
      setAllProducts(res?.data?.data || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load products");
    } finally {
      handleLoading(false);
    }
  };

  const getAllUnitMaster = async () => {
    handleLoading(true);
    try {
      const res = await masterApi.getAllUnitMaster({});
      const list = res?.data?.data?.map((item) => ({
        name: item?.name,
        _id: item?._id,
      })) || [];
      setUnitMasterData(list);
    } catch (err) {
      toast.error(err?.message || "Failed to load units");
    } finally {
      handleLoading(false);
    }
  };

  const getAllOrders = async () => {
    handleLoading(true);
    try {
      const payload = {
        filter: { "orderDetails.delivery_status": "ASSIGNED" },
      };
      const res = await OrderApi.getAllOrders(payload);
      const list =
        res?.data?.data?.orders?.map((item) => ({
          name: item?.order_id,
          _id: item?._id,
        })) || [];
      setAllOrders(list);
    } catch (err) {
      toast.error(err?.message || "Failed to load orders");
    } finally {
      handleLoading(false);
    }
  };

  const findProductById = (id) => allProducts?.find((p) => p?._id === id);

  const toFormData = (values) => {
    const fd = new FormData();

    fd.append("dateOfIssue", values.dateOfIssue || "");
    fd.append("issuedTo", values.issuedTo || "");
    fd.append("vendorLocation", values.vendorLocation || "");
    fd.append("orderReference", values.orderReference || "");
    fd.append("receivedBy", values.receivedBy || "");

    if (values.attachments instanceof File) {
      fd.append("attachments", values.attachments);
    }

    const itemDetails = (values.itemDetails || []).map((x) => ({
      itemCode: x.itemCode || "",
      itemDescription: x.itemDescription || "",
      quantity: Number(x.quantity || 0),
      unit: x.unit || "",
      conditionOfGoods: x.conditionOfGoods || "",
      unitCost: Number(x.unitCost || 0),
      totalCost: Number(x.totalCost || 0),
      comments: x.comments || "",
    }));

    fd.append("itemDetails", JSON.stringify(itemDetails));
    return fd;
  };

  const handleSubmit = async (values) => {
    try {
      const payload = toFormData(values);
      const res = data
        ? await InventoryApi.updateGIV(data?._id, payload)
        : await InventoryApi.createGIV(payload);

      getAllData?.();
      toast.success(data ? "GIV updated successfully" : "GIV created successfully");
      handleClose?.();
      return res;
    } catch (error) {
      console.error("Error while saving GIV:", error);
      toast.error("Failed to save GIV. Please try again.");
    }
  };

  useEffect(() => {
    getAllProducts();
    getAllUnitMaster();
    getAllOrders();
  }, []);

  if (!open) return null;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, handleChange, setFieldValue, isSubmitting }) => (
        <Form className="h-full">
          <SidebarField
            handleClose={handleClose}
            title={data ? "Update Goods Issue Voucher (GIV)" : "Add Goods Issue Voucher (GIV)"}
            button1={<Button text="Cancel" type="button" onClick={handleClose} variant="outline" />}
            button2={
              <Button
                text="Submit"
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              />
            }
            setHeaderWdith={setIsFullWidth}
          >
            <div className="p-5 md:p-6 space-y-8 bg-gray-50/40">
              {/* Header Information */}
              <div
                className={`grid gap-5 ${
                  isFullWidth
                    ? "md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    : "md:grid-cols-2"
                }`}
              >
                {data && (
                  <div>
                    <FormField label="Issue Number" disabledHelp>
                      <TextInput
                        name="issueNumber"
                        value={values.issueNumber}
                        disabled
                        className="bg-gray-100 cursor-not-allowed"
                      />
                    </FormField>
                  </div>
                )}

                <div>
                  <FormField label="Date of Issue" required>
                    <TextInput
                      name="dateOfIssue"
                      type="date"
                      value={values.dateOfIssue} onChange={handleChange}
                      className="focus:ring-2 focus:ring-red-400 focus:border-red-400"
                    />
                  </FormField>
                </div>

                <div>
                  <FormField label="Issued To" required>
                    <TextInput name="issuedTo" value={values.issuedTo} onChange={handleChange} className="focus:ring-2 focus:ring-red-400" />
                  </FormField>
                </div>

                <div>
                  <FormField label="Order Reference" required>
                    <select
                      name="orderReference"
                      value={values.orderReference}
                      onChange={handleChange}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-400 focus:ring-red-400 sm:text-sm h-10 px-3"
                    >
                      <option value="">Select Order Reference</option>
                      {allOrders.map((order) => (
                        <option key={order._id} value={order._id}>
                          {order.name}
                        </option>
                      ))}
                    </select>
                    <ErrorMessage
                      name="orderReference"
                      component="div"
                      className="mt-1 text-xs text-red-600"
                    />
                  </FormField>
                </div>

                <div>
                  <FormField label="Customer Location" required>
                    <TextInput
                      name="vendorLocation"
                      value={values.vendorLocation}
                      onChange={handleChange}
                      placeholder="Enter customer location"
                      className="focus:ring-2 focus:ring-red-400"
                    />
                  </FormField>
                </div>

                <div>
                  <FormField label="Received By" required>
                    <TextInput name="receivedBy" value={values.receivedBy} onChange={handleChange} className="focus:ring-2 focus:ring-red-400" />
                  </FormField>
                </div>

                <div>
                  <FormField label="Attachments">
                    <input
                      type="file"
                      name="attachments"
                      onChange={(e) =>
                        setFieldValue("attachments", e.target.files?.[0] || null)
                      }
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100"
                    />
                  </FormField>
                </div>
              </div>

              {/* Line Items Table */}
              <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-900">Item Details</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Add products being issued with quantities and condition
                  </p>
                </div>

                <FieldArray name="itemDetails">
                  {({ push, remove }) => (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-10">
                              #
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                              Item
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[180px]">
                              Description
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">
                              Qty
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-28">
                              Unit
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-24">
                              Condition
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-28">
                              Unit Cost
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-28">
                              Total
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                              Comments
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider w-20">
                              Action
                            </th>
                          </tr>
                        </thead>

                        <tbody className="bg-white divide-y divide-gray-200">
                          {values.itemDetails.length === 0 ? (
                            <tr>
                              <td colSpan={10} className="px-6 py-10 text-center text-gray-500">
                                No items added yet — click + to add
                              </td>
                            </tr>
                          ) : (
                            values.itemDetails.map((item, index) => (
                              <tr
                                key={index}
                                className="hover:bg-gray-50/70 transition-colors"
                              >
                                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                                  {index + 1}
                                </td>

                                <td className="px-4 py-3">
                                  <select
                                    name={`itemDetails.${index}.itemCode`}
                                    value={values.itemDetails[index].itemCode || ""}
                                    onChange={(e) => {
                                      const id = e.target.value;
                                      setFieldValue(`itemDetails.${index}.itemCode`, id);

                                      const prod = findProductById(id);
                                      const desc = prod?.description?.replace(/<\/?[^>]+(>|$)/g, "") || "";
                                      setFieldValue(`itemDetails.${index}.itemDescription`, desc);
                                    }}
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-400 focus:ring-red-400 sm:text-sm h-9 px-2"
                                  >
                                    <option value="">Select Item</option>
                                    {allProducts.map((product) => (
                                      <option key={product._id} value={product._id}>
                                        {product.name}
                                      </option>
                                    ))}
                                  </select>
                                  <ErrorMessage
                                    name={`itemDetails.${index}.itemCode`}
                                    component="div"
                                    className="mt-1 text-xs text-red-600"
                                  />
                                </td>

                                <td className="px-4 py-3">
                                  <Field
                                    name={`itemDetails.${index}.itemDescription`}
                                    as={TextInput}
                                    className="text-sm focus:ring-red-400"
                                  />
                                  <ErrorMessage
                                    name={`itemDetails.${index}.itemDescription`}
                                    component="div"
                                    className="mt-1 text-xs text-red-600"
                                  />
                                </td>

                                <td className="px-4 py-3">
                                  <Field
                                    name={`itemDetails.${index}.quantity`}
                                    type="number"
                                    as={TextInput}
                                    className="text-sm text-center focus:ring-red-400"
                                  />
                                  <ErrorMessage
                                    name={`itemDetails.${index}.quantity`}
                                    component="div"
                                    className="mt-1 text-xs text-red-600"
                                  />
                                </td>

                                <td className="px-4 py-3">
                                  <Field
                                    name={`itemDetails.${index}.unit`}
                                    as="select"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-400 focus:ring-red-400 sm:text-sm h-9 px-2"
                                  >
                                    <option value="">Select</option>
                                    {unitMasterData.map((unit) => (
                                      <option key={unit._id} value={unit._id}>
                                        {unit.name}
                                      </option>
                                    ))}
                                  </Field>
                                  <ErrorMessage
                                    name={`itemDetails.${index}.unit`}
                                    component="div"
                                    className="mt-1 text-xs text-red-600"
                                  />
                                </td>

                                <td className="px-4 py-3">
                                  <Field
                                    name={`itemDetails.${index}.conditionOfGoods`}
                                    as="select"
                                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-red-400 focus:ring-red-400 sm:text-sm h-9 px-2"
                                  >
                                    <option value="">Select</option>
                                    {[1, 2, 3, 4, 5].map((grade) => (
                                      <option key={grade} value={String(grade)}>
                                        {grade}
                                      </option>
                                    ))}
                                  </Field>
                                  <ErrorMessage
                                    name={`itemDetails.${index}.conditionOfGoods`}
                                    component="div"
                                    className="mt-1 text-xs text-red-600"
                                  />
                                </td>

                                <td className="px-4 py-3">
                                  <Field
                                    name={`itemDetails.${index}.unitCost`}
                                    type="number"
                                    as={TextInput}
                                    className="text-sm text-right focus:ring-red-400"
                                  />
                                  <ErrorMessage
                                    name={`itemDetails.${index}.unitCost`}
                                    component="div"
                                    className="mt-1 text-xs text-red-600"
                                  />
                                </td>

                                <td className="px-4 py-3">
                                  <Field
                                    name={`itemDetails.${index}.totalCost`}
                                    type="number"
                                    as={TextInput}
                                    className="text-sm text-right focus:ring-red-400"
                                  />
                                  <ErrorMessage
                                    name={`itemDetails.${index}.totalCost`}
                                    component="div"
                                    className="mt-1 text-xs text-red-600"
                                  />
                                </td>

                                <td className="px-4 py-3">
                                  <Field
                                    name={`itemDetails.${index}.comments`}
                                    as={TextInput}
                                    className="text-sm focus:ring-red-400"
                                  />
                                </td>

                                <td className="px-4 py-3 text-center">
                                  <button
                                    type="button"
                                    onClick={() => remove(index)}
                                    className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 transition"
                                  >
                                    <IoMdRemoveCircleOutline size={22} />
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>

                      {/* Add Row Button */}
                      <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
                        <button
                          type="button"
                          onClick={() =>
                            push({
                              itemCode: "",
                              itemDescription: "",
                              quantity: "",
                              unit: "",
                              conditionOfGoods: "",
                              unitCost: "",
                              totalCost: "",
                              comments: "",
                            })
                          }
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400"
                        >
                          <IoMdAddCircleOutline className="mr-2 h-5 w-5" />
                          Add Item
                        </button>
                      </div>
                    </div>
                  )}
                </FieldArray>
              </div>
            </div>
          </SidebarField>
        </Form>
      )}
    </Formik>
  );
}

export default AddGIV;
